import { useCallback, useState } from 'react';

interface LocationData {
  country: string;
  city?: string;
  isLoading: boolean;
  error: string | null;
}

interface UseLocationReturn extends LocationData {
  requestLocation: () => Promise<void>;
}

export function useLocation(): UseLocationReturn {
  const [locationData, setLocationData] = useState<LocationData>({
    country: '',
    city: '',
    isLoading: false,
    error: null,
  });

  const requestLocation = useCallback(async (): Promise<void> => {
    if (!('geolocation' in navigator)) {
      setLocationData({
        country: '',
        city: '',
        isLoading: false,
        error: 'Location is not supported by this browser. Choose a city instead.',
      });
      return;
    }

    setLocationData((current) => ({ ...current, isLoading: true, error: null }));

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          enableHighAccuracy: false,
        });
      });
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
      );

      if (!response.ok) {
        throw new Error('Could not determine your city');
      }

      const data = await response.json();
      setLocationData({
        country: data.countryName || '',
        city: data.city || data.locality || '',
        isLoading: false,
        error: null,
      });
    } catch {
      setLocationData({
        country: '',
        city: '',
        isLoading: false,
        error: 'We could not access your location. You can still choose a city manually.',
      });
    }
  }, []);

  return { ...locationData, requestLocation };
}
